import { Nullable, PartialNullable } from "../../Extensions";
import { IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property } from "../../RadObject";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

import "./Time.scss";

export type TimePropertyValues =
    InputControlPropertyValues
    & {
    };

export type TimeProperties = {
    [name in keyof TimePropertyValues]: Property<TimePropertyValues[name]>
};

export interface ITime extends IInputControl, TimeProperties
{
}

export class Time extends InputControl implements ITime
{
    public static override classNamespace = NAMESPACE + "Time";
    public static override className = "Time";

    // #region Global Variables

    protected $$valueElement?: JNative<HTMLInputElement>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TimePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-time ui input form");
    }

    // #endregion

    // #region Protected Methods

    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        const
            elementValue = this._elementValueGet();

        this.value.set(elementValue ? `${elementValue}:00.0000000` : null);
    }
    protected _elementValueGet(): Nullable<string>
    {
        return this.$$valueElement?.val()  ?? null;
    }
    protected _elementValueSet(value: Nullable<string>)
    {
        this.__processElementValueChanged = false;
        this.$$valueElement?.val(value?.substring(0, 5) ?? null);
        this.__processElementValueChanged = true;
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TimePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.text.overrideGet(next =>
        {
            const
                value = this.value.get();

            if (!value)
                return null;

            if (typeof value !== "string")
                return null;

            return value.substring(0, 5);
        });
        this.value.overrideSet((value, next) =>
        {
            if (!value)
                value = null;

            this._elementValueSet(value);
            next(value);
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.$$valueElement?.on("change", { self: this }, this.onValueElementChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement(),
            inputId = `${this.controlId}_input`;

        this.$$valueElement = JNativeStatic.create$$({
            nodeName: "input",
            attributes: {
                id: inputId,
                type: "time",
            }
        }) as JNative<HTMLInputElement>;

        this.$$valueElement.appendTo($$element);

        this.labelTargetId = inputId;

        return $$element;
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        if (change.currentValue)
            this.$$valueElement?.removeAttr("disabled");
        else
            this.$$valueElement?.attr("disabled", "disabled");
    }

    // #endregion

    // #region Event Handlers

    protected onValueElementChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }

    // #endregion
}
